import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Replay,
  KeyboardArrowDown,
  KeyboardArrowUp,
  CheckCircleOutline,
  ErrorOutline,
  Link as LinkIcon,
  AttachFile,
} from '@mui/icons-material';
import { useTranslation } from '../../../../i18n';
import { formatDateTimeISO } from '../../../../utils/dateUtils';
import type { TenderEmail } from '../../../../types/api';
import { getEvaluationColor, getStatusColor } from './emailStatusColors';

interface EmailRowProps {
  email: TenderEmail;
  isExpanded: boolean;
  reprocessingId: string | null;
  onToggleExpanded: (id: string) => void;
  onReprocess: (id: string) => Promise<void>;
}

/**
 * One tender-email table row plus its collapsible detail block (STD-014 split
 * out of TenderEmailsDialog.tsx). Purely presentational — the parent owns the
 * expand/reprocess state and passes it down.
 */
export const EmailRow: React.FC<EmailRowProps> = ({
  email,
  isExpanded,
  reprocessingId,
  onToggleExpanded,
  onReprocess,
}) => {
  const { t } = useTranslation();

  const details = email.evaluation_details || {};
  const confidence =
    typeof details.confidence === 'number' ? Math.round(details.confidence * 100) : null;

  return (
    <>
      {/* Main row */}
      <TableRow
        hover
        sx={{
          cursor: 'pointer',
          '& > *': { borderBottom: isExpanded ? 'unset' : undefined },
        }}
        onClick={() => onToggleExpanded(email.id)}
      >
        <TableCell>
          <IconButton size="small">
            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 600, maxWidth: 300 }} noWrap>
            {email.subject || '(no subject)'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
            {email.from_email}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={email.status}
            size="small"
            color={getStatusColor(email.status)}
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          {email.evaluation ? (
            <Chip
              label={email.evaluation.toUpperCase()}
              size="small"
              color={getEvaluationColor(email.evaluation)}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {confidence !== null ? (
            <Typography variant="body2">{confidence}%</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2">{formatDateTimeISO(email.created_at)}</Typography>
        </TableCell>
        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
          {email.status === 'failed' && (
            <Tooltip title={t('adminToolsPage.tenderEmails.reprocess')}>
              <IconButton
                size="small"
                color="primary"
                onClick={() => onReprocess(email.id)}
                disabled={reprocessingId === email.id}
              >
                {reprocessingId === email.id ? <CircularProgress size={16} /> : <Replay />}
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>

      {/* Expanded detail row */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              {/* Summary */}
              {details.summary && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('adminToolsPage.tenderEmails.summary')}
                  </Typography>
                  <Typography variant="body2">{details.summary}</Typography>
                </Box>
              )}

              {/* Extracted details */}
              {(details.client || details.deadline || details.budget) && (
                <Box sx={{ mb: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {details.client && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('adminToolsPage.tenderEmails.client')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {details.client}
                      </Typography>
                    </Box>
                  )}
                  {details.deadline && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('adminToolsPage.tenderEmails.deadline')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {details.deadline}
                      </Typography>
                    </Box>
                  )}
                  {details.budget && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('adminToolsPage.tenderEmails.budget')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {details.budget}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Key factors */}
              {Array.isArray(details.key_factors) && details.key_factors.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('adminToolsPage.tenderEmails.keyFactors')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {details.key_factors!.map((factor, i) => (
                      <Chip
                        key={i}
                        label={`${factor.positive ? '+' : '−'} ${factor.factor}`}
                        size="small"
                        color={factor.positive ? 'success' : 'error'}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Scraped Links */}
              {Array.isArray(details.scraped_links) && details.scraped_links.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    <LinkIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                    {t('adminToolsPage.tenderEmails.scrapedLinks')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {details.scraped_links!.map((link, i) => (
                      <Chip
                        key={i}
                        icon={
                          link.fetched ? (
                            link.success ? (
                              <CheckCircleOutline sx={{ fontSize: 14 }} />
                            ) : (
                              <ErrorOutline sx={{ fontSize: 14 }} />
                            )
                          ) : undefined
                        }
                        label={`${link.domain}${
                          link.success
                            ? ` (${Math.round(link.char_count / 1000)}k chars)`
                            : link.error
                              ? ` — ${link.error}`
                              : ''
                        }`}
                        size="small"
                        color={link.success ? 'success' : link.fetched ? 'error' : 'default'}
                        variant="outlined"
                        component="a"
                        href={link.resolved_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* PDF Attachments */}
              {Array.isArray(details.attachments) && details.attachments.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    <AttachFile sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                    {t('adminToolsPage.tenderEmails.attachments')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {details.attachments!.map((att, i) => (
                      <Chip
                        key={i}
                        icon={
                          att.success ? (
                            <CheckCircleOutline sx={{ fontSize: 14 }} />
                          ) : (
                            <ErrorOutline sx={{ fontSize: 14 }} />
                          )
                        }
                        label={`${att.filename}${
                          att.success
                            ? ` (${att.pages} ${t('adminToolsPage.tenderEmails.pages')})`
                            : att.error
                              ? ` — ${att.error}`
                              : ''
                        }`}
                        size="small"
                        color={att.success ? 'success' : 'error'}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Individual Tenders */}
              {Array.isArray(details.tenders) && details.tenders.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('adminToolsPage.tenderEmails.individualTenders')}
                  </Typography>
                  <TableContainer sx={{ mt: 0.5 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('adminToolsPage.tenderEmails.tenderTitle')}</TableCell>
                          <TableCell>
                            {t('adminToolsPage.tenderEmails.tableHeaders.evaluation')}
                          </TableCell>
                          <TableCell>
                            {t('adminToolsPage.tenderEmails.tableHeaders.confidence')}
                          </TableCell>
                          <TableCell>{t('adminToolsPage.tenderEmails.client')}</TableCell>
                          <TableCell>{t('adminToolsPage.tenderEmails.deadline')}</TableCell>
                          <TableCell>{t('adminToolsPage.tenderEmails.summary')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {details.tenders!.map((tender, i) => (
                          <TableRow key={i}>
                            <TableCell>
                              {tender.url ? (
                                <Typography
                                  variant="body2"
                                  component="a"
                                  href={tender.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ color: 'primary.main' }}
                                >
                                  {tender.title}
                                </Typography>
                              ) : (
                                <Typography variant="body2">{tender.title}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={tender.evaluation.toUpperCase()}
                                size="small"
                                color={getEvaluationColor(tender.evaluation)}
                              />
                            </TableCell>
                            <TableCell>{Math.round(tender.confidence * 100)}%</TableCell>
                            <TableCell>
                              <Typography variant="body2">{tender.client || '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{tender.deadline || '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 300 }}>
                                {tender.summary}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Full reasoning */}
              {email.evaluation_reasoning && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('adminToolsPage.tenderEmails.reasoning')}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    {email.evaluation_reasoning}
                  </Typography>
                </Box>
              )}

              {/* Reply status */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {t('adminToolsPage.tenderEmails.replySent')}:
                </Typography>
                <Chip
                  label={email.reply_sent ? 'Yes' : 'No'}
                  size="small"
                  color={email.reply_sent ? 'success' : 'default'}
                  variant="outlined"
                />
                {email.processing_completed_at && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {t('adminToolsPage.tenderEmails.processedAt')}:
                    </Typography>
                    <Typography variant="caption">
                      {formatDateTimeISO(email.processing_completed_at)}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
